import { Link } from 'react-router-dom'
import {
  Users,
  Boxes,
  ClipboardList,
  CheckSquare,
  MessageSquare,
  FileText,
  Search,
} from 'lucide-react'
import { SearchResult } from '@/types/search'
import { SearchGroupBy } from '@/pages/search/SearchResultsPage'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils/cn'

interface SearchResultsGroupingProps {
  nodes?: SearchResult[]
  tasks?: SearchResult[]
  projects?: SearchResult[]
  messages?: SearchResult[]
  groupBy: SearchGroupBy
  className?: string
}

export function SearchResultsGrouping({ nodes, groupBy, className }: SearchResultsGroupingProps) {
  const results = nodes || []

  if (results.length === 0) {
    return null
  }

  if (groupBy === 'none') {
    return (
      <div className={cn('space-y-3', className)}>
        {results.map((result) => (
          <SearchResultCard key={result.id} result={result} />
        ))}
      </div>
    )
  }

  // Group by type
  const groupedResults = results.reduce(
    (acc, result) => {
      const type = result.type
      if (!acc[type]) {
        acc[type] = []
      }
      acc[type].push(result)
      return acc
    },
    {} as Record<string, SearchResult[]>
  )

  const getTypeIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'user':
        return <Users className="h-5 w-5" />
      case 'workspace':
        return <Boxes className="h-5 w-5" />
      case 'project':
        return <ClipboardList className="h-5 w-5" />
      case 'task':
        return <CheckSquare className="h-5 w-5" />
      case 'message':
        return <MessageSquare className="h-5 w-5" />
      case 'document':
        return <FileText className="h-5 w-5" />
      default:
        return <Search className="h-5 w-5" />
    }
  }

  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'user':
        return 'Users'
      case 'workspace':
        return 'Workspaces'
      case 'project':
        return 'Projects'
      case 'task':
        return 'Tasks'
      case 'message':
        return 'Messages'
      case 'document':
        return 'Documents'
      default:
        return 'Results'
    }
  }

  return (
    <div className={cn('space-y-6', className)}>
      {Object.entries(groupedResults).map(([type, typeResults]) => (
        <Card key={type}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="text-xl">{getTypeIcon(type as SearchResult['type'])}</span>
              {getTypeLabel(type as SearchResult['type'])}
              <Badge variant="secondary" className="ml-auto">
                {typeResults.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {typeResults.map((result) => (
                <SearchResultCard key={result.id} result={result} />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

interface SearchResultCardProps {
  result: SearchResult
}

function SearchResultCard({ result }: SearchResultCardProps) {
  return (
    <Link
      to={result.url}
      className="block p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate">{result.title}</h3>
          {result.subtitle && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{result.subtitle}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge variant="outline" className="text-xs">
            {getResultTypeLabel(result.type)}
          </Badge>
        </div>
      </div>
    </Link>
  )
}

function getResultTypeLabel(type: SearchResult['type']) {
  switch (type) {
    case 'user':
      return 'User'
    case 'workspace':
      return 'Workspace'
    case 'project':
      return 'Project'
    case 'task':
      return 'Task'
    case 'message':
      return 'Message'
    case 'document':
      return 'Document'
    default:
      return 'Result'
  }
}
